import React, {
  FC,
  FormEvent,
  ReactNode,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import OvCompactButton from '../atoms/OvCompactButton';
import OvErrorMessage from '../atoms/OvErrorMessage';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvRecordEditDialogProps {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  onSubmit?: () => void;
  isDirty?: boolean;
  isSaving?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  submitLabel?: string;
  busyLabel?: string;
  submitTone?: 'primary' | 'danger';
  isSubmitDisabled?: boolean;
  submitDescribedBy?: string;
  showLiveNotice?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md';
  isCancelFocused?: boolean;
}

const OvRecordEditDialog: FC<OvRecordEditDialogProps> = ({
  isOpen,
  title,
  children,
  onClose,
  onSubmit,
  isDirty = false,
  isSaving = false,
  hasError = false,
  errorMessage,
  submitLabel,
  busyLabel,
  submitTone = 'primary',
  isSubmitDisabled = false,
  submitDescribedBy,
  showLiveNotice = false,
  maxWidth = 'sm',
  isCancelFocused = false,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const focusBeforeDiscard = useRef<HTMLElement | null>(null);

  const requestClose = () => {
    if (isSaving) {
      return;
    }

    if (!isDirty) {
      onClose();
      return;
    }

    focusBeforeDiscard.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setIsConfirmingDiscard(true);
  };

  const keepEditing = () => {
    setIsConfirmingDiscard(false);
    window.setTimeout(() => focusBeforeDiscard.current?.focus());
  };

  const discard = () => {
    setIsConfirmingDiscard(false);
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // React bubbles portal events to the opener's tree, which may be a form.
    event.stopPropagation();

    if (onSubmit && !isSaving && !isSubmitDisabled) {
      onSubmit();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isConfirmingDiscard ? keepEditing : requestClose}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby={titleId}
      TransitionProps={{ onExited: () => setIsConfirmingDiscard(false) }}
      PaperProps={{
        style: {
          backgroundColor: Colours.WHITE,
          borderRadius: Variables.borderRadius.LARGE,
        },
      }}
    >
      <Head>
        <h2 id={titleId}>
          {isConfirmingDiscard
            ? t('common.recordEditDialog.discardTitle')
            : title}
        </h2>
        {!isConfirmingDiscard && (
          <IconButton
            aria-label={t('common.actions.close')}
            onClick={requestClose}
            disabled={isSaving}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Head>

      {isConfirmingDiscard && (
        <Actions>
          <OvCompactButton tone="secondary" onClick={keepEditing} autoFocus>
            {t('common.recordEditDialog.keepEditing')}
          </OvCompactButton>
          <OvCompactButton tone="danger" onClick={discard}>
            {t('common.recordEditDialog.discard')}
          </OvCompactButton>
        </Actions>
      )}

      <Form noValidate onSubmit={handleSubmit} hidden={isConfirmingDiscard}>
        <Body>
          {children}
          {showLiveNotice && (
            <LiveNotice>{t('common.recordEditDialog.liveNotice')}</LiveNotice>
          )}
          {(hasError || errorMessage) && (
            <div role="alert">
              <OvErrorMessage
                message={
                  errorMessage ?? t('common.recordEditDialog.saveFailed')
                }
              />
            </div>
          )}
        </Body>
        <Actions>
          <OvCompactButton
            tone="secondary"
            onClick={requestClose}
            disabled={isSaving}
            autoFocus={isCancelFocused}
          >
            {t(onSubmit ? 'common.actions.cancel' : 'common.actions.close')}
          </OvCompactButton>
          {onSubmit && (
            <OvCompactButton
              type="submit"
              tone={submitTone}
              isBusy={isSaving}
              busyLabel={busyLabel ?? t('common.recordEditDialog.saving')}
              disabled={isSubmitDisabled}
              aria-describedby={submitDescribedBy}
            >
              {submitLabel ?? t('common.recordEditDialog.save')}
            </OvCompactButton>
          )}
        </Actions>
      </Form>
    </Dialog>
  );
};

export default OvRecordEditDialog;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0.5rem 1.5rem;
  color: ${Colours.OV_BASE};

  h2 {
    margin: 0;
    font-size: ${Variables.fontSizes.MLARGE};
    line-height: ${Variables.lineHeights.MLARGE};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  min-height: 0;

  &[hidden] {
    display: none;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  padding: 0.5rem 1.5rem 1rem;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
`;

const LiveNotice = styled.p`
  margin: 0;
  color: ${Colours.OV_CHARCOAL};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem 1.5rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;
